import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * SupportChatBubble — floating helper bubble that customers can tap on any
 * storefront page. Powered by whichever model the admin chose for the
 * `support_chatbot` feature (default OpenAI, most cost-effective is Claude
 * Haiku 4.5).
 *
 * Rolling 20-turn window is kept client-side and pushed with every message so
 * the backend stays stateless. Session ID is persisted in sessionStorage so
 * the same conversation resumes across page navigations within the tab.
 */
const SUGGESTED = [
  'What are your delivery windows?',
  'Which vendors are open right now?',
  'How do refunds work?',
  'How do tips get to the driver?',
];

const SupportChatBubble = ({ vendorId = null }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = sessionStorage.getItem('shipitnow.support.messages');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem('shipitnow.support.sid') || null);
  const scrollRef = useRef(null);

  useEffect(() => {
    try { sessionStorage.setItem('shipitnow.support.messages', JSON.stringify(messages.slice(-20))); } catch { /* quota / private mode */ }
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  useEffect(() => {
    if (sessionId) sessionStorage.setItem('shipitnow.support.sid', sessionId);
  }, [sessionId]);

  // Show only on public storefront routes (hide on admin/vendor/driver portals + auth pages)
  const hiddenPathPrefixes = ['/admin', '/vendor-admin', '/driver', '/login', '/signup', '/onboarding'];
  const shouldHide = !user || hiddenPathPrefixes.some((p) => pathname.startsWith(p));
  if (shouldHide) return null;

  const send = async (text) => {
    const clean = (text || input).trim();
    if (!clean || busy) return;
    const nextHistory = [...messages, { role: 'user', content: clean }];
    setMessages(nextHistory);
    setInput('');
    setBusy(true);
    try {
      const { data } = await api.post('/support/chat', {
        message: clean,
        history: messages.slice(-20),
        vendor_id: vendorId || undefined,
        session_id: sessionId || undefined,
      });
      setSessionId(data.session_id);
      setMessages([...nextHistory, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Support bot is offline right now.');
      setMessages(nextHistory); // keep the user's message; drop the failed reply
    } finally { setBusy(false); }
  };

  const clear = () => {
    setMessages([]); setSessionId(null);
    sessionStorage.removeItem('shipitnow.support.messages');
    sessionStorage.removeItem('shipitnow.support.sid');
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-900/20 flex items-center justify-center transition-all hover:scale-105"
          title="Ask the ShipItNow helper"
          data-testid="support-chat-open"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-amber-400 rounded-full ring-2 ring-white animate-pulse" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[92vw] sm:w-[380px] h-[520px] max-h-[80vh] rounded-2xl bg-white border border-slate-200 shadow-2xl flex flex-col overflow-hidden" data-testid="support-chat-panel">
          <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold leading-none">ShipItNow helper</div>
                <div className="text-[10px] text-emerald-100 mt-0.5">Instant answers · powered by AI</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button onClick={clear} className="text-emerald-100 hover:text-white text-[11px] px-2 py-1 rounded-md hover:bg-white/10" data-testid="support-chat-clear">
                  Clear
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-white/90 hover:text-white p-1 rounded-md hover:bg-white/10" data-testid="support-chat-close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div>
                <p className="text-sm text-slate-700 font-medium">Hi! I can help with menus, delivery windows, wallet top-ups, and more.</p>
                <p className="text-xs text-slate-500 mt-1">Try one of these:</p>
                <div className="mt-2 space-y-1.5">
                  {SUGGESTED.map((s) => (
                    <button key={s} onClick={() => send(s)} className="w-full text-left text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-emerald-500 hover:text-emerald-700" data-testid={`support-chat-suggestion-${SUGGESTED.indexOf(s)}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-snug ${m.role === 'user' ? 'bg-emerald-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}
                  data-testid={`support-chat-msg-${i}`}
                >{m.content}</div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 text-slate-500 rounded-2xl rounded-bl-sm px-3 py-2 text-sm flex items-center gap-2" data-testid="support-chat-typing">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          <form
            className="border-t border-slate-200 p-3 flex items-center gap-2 bg-white"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about menus, delivery, refunds…"
              className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-emerald-500"
              disabled={busy}
              data-testid="support-chat-input"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="h-10 w-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white flex items-center justify-center"
              data-testid="support-chat-send"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default SupportChatBubble;
